/* eslint-disable @typescript-eslint/no-explicit-any */
import {Result} from 'neverthrow';
import {AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';

export enum ApiErrorKind {
  UnauthenticatedError,
  NotFoundError,
  ValidationError,
  InternalServerError,
  BadResponseError,
  TimeoutError,
  RateLimitError,
  InvalidInstance,
}

interface BadApiResponse {
  kind: ApiErrorKind;
  response: AxiosResponse;
}

export interface InvalidInstanceError {
  kind: ApiErrorKind.InvalidInstance;
}

// This does not extend BadApiResponse because a timeout might not have a response to send back
export interface TimeoutError {
  kind: ApiErrorKind.TimeoutError;
  response?: AxiosResponse;
}

export interface InternalServerError extends BadApiResponse {
  kind: ApiErrorKind.InternalServerError;
}

export interface UnauthenticatedError extends BadApiResponse {
  kind: ApiErrorKind.UnauthenticatedError;
}

export interface NotFoundError extends BadApiResponse {
  kind: ApiErrorKind.NotFoundError;
}

export interface ValidationError extends BadApiResponse {
  kind: ApiErrorKind.ValidationError;
  errors: Record<string, string[]>;
}

export interface BadResponseError extends BadApiResponse {
  kind: ApiErrorKind.BadResponseError;
}

export interface RateLimitError extends BadApiResponse {
  kind: ApiErrorKind.RateLimitError;
}

export type ApiError =
  | TimeoutError
  | InternalServerError
  | UnauthenticatedError
  | NotFoundError
  | ValidationError
  | BadResponseError
  | RateLimitError
  | InvalidInstanceError;

export type ApiResult<Data> = Result<AxiosResponse<Data>, ApiError> & {
  mapData<X = void>(mapper: (d: Data) => X): Result<X, ApiError>;
};

export type ApiResponse<Data> = Promise<ApiResult<Data>>;

export type UnknownServerData = Record<string, any>;

export interface ApiClient {
  _unsafe: AxiosInstance;
  get<D = UnknownServerData>(
    url: string,
    config?: AxiosRequestConfig
  ): ApiResponse<D>;
  delete<D = UnknownServerData>(
    url: string,
    config?: AxiosRequestConfig
  ): ApiResponse<D>;
  head<D = UnknownServerData>(
    url: string,
    config?: AxiosRequestConfig
  ): ApiResponse<D>;
  options<D = UnknownServerData>(
    url: string,
    config?: AxiosRequestConfig
  ): ApiResponse<D>;
  post<In, Out = UnknownServerData>(
    url: string,
    data?: In,
    config?: AxiosRequestConfig
  ): ApiResponse<Out>;
  put<In, Out = UnknownServerData>(
    url: string,
    data?: In,
    config?: AxiosRequestConfig
  ): ApiResponse<Out>;
  patch<In, Out = UnknownServerData>(
    url: string,
    data?: In,
    config?: AxiosRequestConfig
  ): ApiResponse<Out>;
}

export type JWT$Server = {
  token: string;
  refresh_token: string;
  expires_in: number;
};

export type JWT = {
  token: string;
  refreshToken: string;
  expiresIn: number;
};

export type DecodedJWT = {
  /** user id */
  sub: number;
  /** issued at */
  iat: number;
  /** expires at */
  exp: number;
  /** practice id */
  pid: number;
  /** timezone identifier */
  zoneinfo: string;
  /** user type */
  subtype: string;
  /** issuer */
  iss: string;
};
